import { TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { fmtR, RPT_AXIS_TICK, RPT_AXIS_TICK_BOLD, RPT_TOOLTIP_STYLE, RPT_BAR_RADIUS_RIGHT } from './constants';

/** Fuel efficiency ranking (horizontal bar chart + table). */
export const FuelEfficiencyCard = ({ fuelEfficiencyData, monthLabel }) => (
  <Card className="card-hover">
    <CardHeader className="pb-2">
      <CardTitle className="text-lg font-heading font-semibold flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-green-600" />
        Fuel Efficiency — {monthLabel}
      </CardTitle>
    </CardHeader>
    <CardContent>
      {fuelEfficiencyData.length > 0 ? (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={fuelEfficiencyData} layout="vertical" margin={{ left: 100 }}>
              <XAxis type="number" axisLine={false} tickLine={false} tick={RPT_AXIS_TICK} tickFormatter={(v) => `${v} km/L`} />
              <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={RPT_AXIS_TICK_BOLD} width={100} />
              <Tooltip formatter={(value) => [`${value} km/L`, 'Efficiency']} contentStyle={RPT_TOOLTIP_STYLE} />
              <Bar dataKey="km_per_liter" radius={RPT_BAR_RADIUS_RIGHT}>
                {fuelEfficiencyData.map((entry) => (
                  <Cell key={entry.vehicle_id} fill={entry.km_per_liter >= 10 ? '#16A34A' : entry.km_per_liter >= 7 ? '#F59E0B' : '#DC2626'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-6 overflow-x-auto">
            <table className="w-full" data-testid="efficiency-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Vehicle</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">km/L</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Total km</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Liters</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Fuel Cost</th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Cost / km</th>
                </tr>
              </thead>
              <tbody>
                {fuelEfficiencyData.map(v => {
                  const costPKm = v.total_km > 0 ? (v.fuel / v.total_km) : null;
                  return (
                    <tr key={v.vehicle_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4">
                        <p className="font-medium text-slate-900">{v.name}</p>
                        <p className="text-sm text-slate-500 font-data">{v.registration}</p>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`font-data font-bold text-lg ${v.km_per_liter >= 10 ? 'text-green-600' : v.km_per_liter >= 7 ? 'text-amber-600' : 'text-red-600'}`}>
                          {v.km_per_liter}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-data text-slate-600">{v.total_km?.toLocaleString()}</td>
                      <td className="py-3 px-4 text-right font-data text-slate-600">{v.liters?.toFixed(1)}</td>
                      <td className="py-3 px-4 text-right font-data text-blue-600">{fmtR(v.fuel)}</td>
                      <td className="py-3 px-4 text-right font-data font-bold text-slate-700">{costPKm ? fmtR(costPKm) : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="h-[300px] flex items-center justify-center text-slate-400">
          <div className="text-center">
            <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>No fuel efficiency data for {monthLabel}</p>
            <p className="text-sm mt-1">Requires at least 2 fuel entries with odometer readings</p>
          </div>
        </div>
      )}
    </CardContent>
  </Card>
);
